import React, { useEffect, useState, useMemo } from "react";
import { fetchDateOfUploadForReview } from "../../services/dataModule/dataReview.js";
import Grid from "@mui/material/Grid2";
import { Container, TextField } from "@mui/material";
import CustomerByUpload from "./CustomerByUpload.jsx";
import { handleSort, getComparator } from "../../hooks/useTable.js";
import DataReviewTable from "./DataReviewTable.jsx";
import { useClientTimeZone, formatDateInZone } from "../../utils/clienttimezone";
import {
	DATA_REVIEW_CONSTANTS,
	DATA_REVIEW_TABLE_COLUMNS,
} from "../../common/constants/dataModule/dataReviewConstants.js";
import {
	SIZES,
	VARIANTS,
	SORT_ORDERS,
} from "../../common/constants/commonConstants.js";

const DataReview = () => {
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [order, setOrder] = useState(SORT_ORDERS.DESCENDING);
	const [orderBy, setOrderBy] = useState("uploadDate");
	const [dateOfUploadForReview, setDateOfUploadForReview] = React.useState([]);
	const [selectedUploadDate, setSelectedUploadDate] = React.useState(null);
	const [selectedCampaignId, setSelectedCampaignId] = React.useState(null);
	const [isLoading, setIsLoading] = React.useState(false);
	const [searchTerm, setSearchTerm] = React.useState("");

	// Upload dates render in the configured client zone, not the browser's.
	// If uploaddate comes back date-only ("2026-08-03"), formatDateInZone
	// passes it through unconverted — a calendar date has no zone.
	const { timeZone } = useClientTimeZone();

	useEffect(() => {
		setPage(0);
	}, [searchTerm]);

	useEffect(() => {
		const loadUploadDates = async () => {
			try {
				setIsLoading(true);
				const fetchedData = await fetchDateOfUploadForReview();
				setDateOfUploadForReview(fetchedData);
			} catch (error) {
				console.error("Error fetching upload dates:", error);
			} finally {
				setIsLoading(false);
			}
		};
		loadUploadDates();
	}, []);

	const dataReviewList = dateOfUploadForReview.map((uploadDate) => {
		const label =
			formatDateInZone(uploadDate.uploaddate, timeZone) +
			" - " + uploadDate.uploaddate_count + " record(s)";
		return {
			...uploadDate,
			uploaddate: label,
			// also expose camelCase property matching column id for sorting
			uploadDate: label,
			originaluploaddate: uploadDate.uploaddate,
			// numeric timestamp used for proper date sorting. Instant-based, so
			// the ordering is the same regardless of display zone.
			sortDate: (() => {
				const t = Date.parse(uploadDate.uploaddate);
				return isNaN(t) ? 0 : t;
			})(),
		};
	});

	const { uploadDateToDisplay, filteredUploadDateListLength } = useMemo(() => {
		const search = searchTerm.toLowerCase().trim();

		const filteredUplodaDateList = dataReviewList.filter((uploadDate) => {
			const campaignName = uploadDate.campaignName?.toLowerCase() || "";
			return campaignName.includes(search);
		});
		// when ordering by the displayed uploaddate, use numeric sortDate for correct chronological order
		const comparatorField = orderBy === "uploadDate" ? "sortDate" : orderBy;
		// if sorting by sortDate (uploaddate header), use numeric sort to avoid string comparison issues
		let sortedList;
		if (comparatorField === "sortDate") {
			sortedList = filteredUplodaDateList.sort((a, b) => {
				const av = a.sortDate || 0;
				const bv = b.sortDate || 0;
				return order === "asc" ? av - bv : bv - av;
			});
		} else {
			sortedList = filteredUplodaDateList.sort(getComparator(order, comparatorField));
		}

		return {
			uploadDateToDisplay: sortedList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
			filteredUploadDateListLength: filteredUplodaDateList.length,
		};
	}, [dataReviewList, searchTerm, order, orderBy, page, rowsPerPage]);

	if (selectedUploadDate) {
		return (
			<CustomerByUpload
				uploaddate={selectedUploadDate}
				campaignId={selectedCampaignId}
				onBack={() => setSelectedUploadDate(null)}
			/>
		);
	}
	
	const handleReviewCustomerClick = (row) => {
		setSelectedUploadDate(row.originaluploaddate);
		setSelectedCampaignId(row.campaignid);
	};

	const handleRequestSort = (property) => {
		handleSort(order, orderBy, property, setOrder, setOrderBy);
	};

	return (
		<>
			<Container width="xl" disableGutters>
				<Grid size={SIZES.GROW} sx={{ mb: 2 }}>
					<TextField
						fullWidth
						label={DATA_REVIEW_CONSTANTS.SEARCH_PLACEHOLDER}
						variant={VARIANTS.OUTLINED}
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						size={SIZES.SMALL}
					/>
				</Grid>
				<DataReviewTable
					page={page}
					order={order}
					setPage={setPage}
					orderBy={orderBy}
					rowsPerPage={rowsPerPage}
					isLoading={isLoading}
					dataReviewToDisplay={uploadDateToDisplay}
					setRowsPerPage={setRowsPerPage}
					columns={DATA_REVIEW_TABLE_COLUMNS}
					onReview={handleReviewCustomerClick}
					handleRequestSort={handleRequestSort}
					filteredUploadDateListLength={filteredUploadDateListLength}
				/>
			</Container>
		</>
	);
};

export default DataReview;